import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PloegInschrijvingFields } from "./PloegInschrijvingFields";
import {
  useInschrijvingActies,
  type Inschrijving,
  type PloegInschrijving,
} from "./useInschrijving";

/**
 * A modal rather than a pane under the list: a ploeg is changed occasionally and the list is read
 * constantly, and a form that unfolds in place pushes it further down every time.
 */
export function WijzigPloeg({
  inschrijving,
  ploeg,
  korpsId,
  gesloten,
  onSluiten,
}: {
  inschrijving: Inschrijving;
  /** The ploeg being changed. Absent closes the modal. */
  ploeg: PloegInschrijving | undefined;
  korpsId: string;
  gesloten: boolean;
  onSluiten: () => void;
}) {
  const { t } = useTranslation(["ploegen", "common"]);
  const { opslaan, intrekken } = useInschrijvingActies(
    inschrijving.inschrijfperiodeId,
    korpsId,
  );

  if (!ploeg) {
    return null;
  }

  const bezig = opslaan.isPending || intrekken.isPending;

  return (
    <Dialog
      open
      onOpenChange={(volgende) => {
        if (!volgende) {
          opslaan.reset();
          intrekken.reset();
          onSluiten();
        }
      }}
    >
      {/* Not focusing the ploegnaam is `DialogContent`'s own doing now - see lib/focus.ts. */}
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{ploeg.name}</DialogTitle>
          <DialogDescription>{t("wijzig.description")}</DialogDescription>
        </DialogHeader>

        <PloegInschrijvingFields
          key={ploeg.ploegInschrijvingId}
          id={`ploeg-${ploeg.ploegId}`}
          inschrijving={inschrijving}
          velden={{
            name: ploeg.name === ploeg.defaultName ? "" : ploeg.name,
            klasseIds: ploeg.klasseIds,
            voorkeuren: ploeg.voorkeuren,
            wilSamenOpEenDag: ploeg.wilSamenOpEenDag,
            deeltVrVoertuig: ploeg.deeltVrVoertuig,
          }}
          standaardNaam={ploeg.defaultName}
          submitLabel={t("common:actions.save")}
          pending={opslaan.isPending}
          error={opslaan.error}
          gesloten={gesloten}
          onSubmit={(velden) =>
            opslaan.mutate(
              { ploegId: ploeg.ploegId, ...velden },
              { onSuccess: onSluiten },
            )
          }
          /* A whole row from Opslaan: withdrawing is the rare, irreversible act and should not
            be what your hand finds on the way to saving. The rows go for good, so it asks first. */
          leadingAction={
            <IntrekkenActie
              t={t}
              ploeg={ploeg}
              bezig={bezig}
              gesloten={gesloten}
              intrekken={intrekken}
              onSluiten={onSluiten}
            />
          }
        />

        {intrekken.error && (
          <p className="text-sm text-destructive">{intrekken.error.message}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The rare, irreversible withdraw: its own confirm dialog, separate from Opslaan's own busy state. */
function IntrekkenActie({
  t,
  ploeg,
  bezig,
  gesloten,
  intrekken,
  onSluiten,
}: {
  t: TFunction<["ploegen", "common"]>;
  ploeg: PloegInschrijving;
  bezig: boolean;
  gesloten: boolean;
  intrekken: ReturnType<typeof useInschrijvingActies>["intrekken"];
  onSluiten: () => void;
}) {
  return (
    <ConfirmDelete
      trigger={
        <Button
          type="button"
          variant="ghost"
          className="text-destructive"
          disabled={bezig || gesloten}
        >
          {intrekken.isPending
            ? t("common:actions.busy")
            : t("wijzig.intrekken")}
        </Button>
      }
      title={t("wijzig.intrekkenVraag", { naam: ploeg.name })}
      description={t("wijzig.intrekkenDescription")}
      confirmLabel={t("wijzig.intrekkenConfirm", { naam: ploeg.name })}
      onConfirm={() =>
        intrekken.mutate(ploeg.ploegId, { onSuccess: onSluiten })
      }
    />
  );
}
